import React, { createContext, useContext, useState } from 'react';
import { getGlobalIntl } from '../../reduxConnectedIntlProvider';
import { copyLocaleOf } from '../../utils/localeCopy';
import type { CopyLocale } from '../../utils/localeCopy';

/**
 * The language of the quiz content itself — questions, options, reports — on
 * both public quiz pages (保障小測驗, 招募測驗) and wherever the adviser's
 * screens quote that content back.
 *
 * Every piece of quiz copy is a `Txt`: the same words in the app's three
 * locales, written side by side with `L(繁, 简, English)` so a translation can
 * never go missing without the type checker saying so. Simplified keeps the
 * Hong Kong terms (按揭、強積金 → 按揭、强积金): the readers are Hong Kong clients
 * who read Simplified, not Mainland ones.
 *
 * The page's language: the one the adviser chose for the link, else the app's;
 * the client can switch with `QuizLangSwitcher`.
 */
export type QuizLang = CopyLocale;
export type Txt = Record<QuizLang, string>;

export const QUIZ_LANGS: QuizLang[] = ['zh-Hant-HK', 'zh', 'en'];

export const L = (tc: string, sc: string, en: string): Txt => ({ 'zh-Hant-HK': tc, zh: sc, en });

/** The same words in every language — numbers, emoji, brand names. */
export const same = (text: string): Txt => L(text, text, text);

export const quizLangOf = copyLocaleOf;

/** Reads one language of a `Txt`. */
export const tx = (text: Txt, lang: QuizLang): string => text[lang];

/** Joins a list the way each language does: 、 in Chinese, a comma in English. */
export const listJoin = (items: string[], lang: QuizLang): string => items.join(lang === 'en' ? ', ' : '、');

const QuizLangContext = createContext<QuizLang>('zh-Hant-HK');

export const QuizLangProvider = QuizLangContext.Provider;

/** The language the surrounding quiz page is showing. */
export const useQuizLang = (): QuizLang => useContext(QuizLangContext);

/** Shorthand inside a page: `t(L(...))` or `t(option.label)`. */
export const useTx = () => {
  const lang = useQuizLang();
  return (text: Txt) => text[lang];
};

/**
 * A public quiz page's language: the client's own pick, else the language the
 * adviser chose for the link, else the preview's `?lang=`, else the app's.
 * `restored` is a pick brought back from a saved draft (quizDraft), so a client
 * who switched to EN does not come back to Chinese.
 */
export const usePageQuizLang = (linkLocale: string | undefined, search: string, restored: QuizLang | null = null) => {
  const [chosen, setChosen] = useState<QuizLang | null>(restored);
  const preview = new URLSearchParams(search).get('lang');
  const lang = chosen || quizLangOf(linkLocale || preview || getGlobalIntl()?.locale);
  return [lang, setChosen] as const;
};

const SWITCH_LABELS: Record<QuizLang, string> = { 'zh-Hant-HK': '繁', zh: '简', en: 'EN' };

/** 繁 · 简 · EN, top right of the public quiz pages. */
export const QuizLangSwitcher = (props: { lang: QuizLang; onChange: (lang: QuizLang) => void; testId: string }) => (
  <div className="quiz-lang-switch" role="group" aria-label="Language" data-testid={props.testId}>
    {QUIZ_LANGS.map(lang => (
      <button
        key={lang}
        type="button"
        className={`quiz-lang-btn${lang === props.lang ? ' is-active' : ''}`}
        aria-pressed={lang === props.lang}
        data-lang={lang}
        onClick={() => props.onChange(lang)}
      >
        {SWITCH_LABELS[lang]}
      </button>
    ))}
  </div>
);
